import React, { useEffect, useState } from "react";

import {
  Box,
  Autocomplete,
  Chip,
  Divider,
  MenuItem,
  ListSubheader,
  TextField,
} from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import { createFilterOptions } from "@mui/material/Autocomplete";

import { HelpBox } from "@/components/atoms";
import { Variable } from "@/types";
import { checkVariable, toVariableLabel } from "@/utils";

interface VariableSelectionProps {
  variables: Variable[];
  selection: Variable[];
  onSelect: (variables: Variable[]) => void;
  onDelete: (variable: Variable) => void;
  disabled?: boolean;
}

const VariableSelection: React.FC<VariableSelectionProps> = ({
  variables,
  selection,
  onSelect,
  onDelete,
  disabled = false,
}) => {
  const theme = useTheme();

  const [typeOptions, setTypeOptions] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string>("");
  const [variableOptions, setVariableOptions] = useState<Variable[]>([]);

  useEffect(() => {
    setTypeOptions(Array.from(new Set(variables.map((v) => v.tag_type))));
    setVariableOptions(variables.filter((v) => v.tag_type === selectedType));
  }, [variables, selectedType]);

  const handleTypeSelect = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSelectedType(e.target.value);

  const handleVariableSelect = (event: any, variables: Variable[]) => {
    onSelect(variables);
  };

  const filterOptions = createFilterOptions({
    stringify: (option: Variable) => `${option.tag_category} ${option.tag_name}`
  })

  return (
    <>
      <Grid size={6}>
        <TextField
          fullWidth
          id="type-select"
          label="Data Type"
          variant="outlined"
          color="secondary"
          select
          value={selectedType}
          onChange={handleTypeSelect}
          disabled={disabled}
        >
          {typeOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid size={6}>
        <Autocomplete
          id="variable-select"
          multiple
          disabled={disabled}
          disableCloseOnSelect
          color="secondary"
          options={variableOptions}
          value={selection}
          onChange={handleVariableSelect}
          groupBy={(option) => option.tag_category}
          getOptionLabel={(option) => option.tag_name}
          renderGroup={(params) => [
            <Box width="100%">
              <Divider
                textAlign="left"
                sx={{
                  "&::before, &::after": {
                    borderColor: theme.palette.secondary.main,
                  },
                }}
              >
                <ListSubheader
                  key={params.key}
                  disableSticky
                  sx={{
                    textTransform: "uppercase",
                    fontFamily: "cera-pro-bold",
                    color: theme.palette.secondary.main,
                    fontSize: "16px",
                  }}
                >
                  {params.group}
                </ListSubheader>
              </Divider>
            </Box>,
            params.children,
          ]}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label="Variable"
              variant="outlined"
              placeholder="Select Variables"
              color="secondary"
              disabled={disabled}
            />
          )}
          renderTags={() => null}
          filterOptions={filterOptions}
        />
      </Grid>
      <Grid size={12}>
        <Box
          sx={{
            border: 1,
            borderRadius: 1,
            borderColor: theme.palette.divider,
            minHeight: "48px",
            p: 1,
          }}
        >
          {selection.map((variable) => {
            const valid = checkVariable(variable, variables);
            return (
              <HelpBox title={valid ? '' : "This variable no longer exists, you'll need to remove it in order to build this audience"}>
                <Chip
                  key={variable.tag_id}
                  size="small"
                  color={valid ? "secondary" : "error"}
                  label={toVariableLabel(variable)}
                  onDelete={() => onDelete(variable)}
                  sx={{ m: 0.5 }}
                  disabled={disabled}
                  icon={valid ? undefined : <ErrorOutline />}
                />
              </HelpBox>
            )}
          )}
        </Box>
      </Grid>
    </>
  );
};

export default VariableSelection;
